import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { AssetClassEmission } from "../../../models/dto/cat15/cat15.types";
import { assetClassLabelKey } from "../../../components/cat15/labels";
import { formatNumber } from "../format";
import { assetClassOpacity, sortedAssetClasses } from "./borrowerSummary";

interface Props {
  rows: AssetClassEmission[];
}

/**
 * Financed Emission By Asset Class body — Figma 7846:51918: one Chart_Info row
 * per class (190px label, bar track, 88px value, percentage), largest first.
 * Bars scale to the largest class and fade by rank (var(--green) at 100 → 20 % opacity).
 */
const AssetClassBars = ({ rows }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const sorted = sortedAssetClasses(rows);
  const max = Math.max(0, ...sorted.map((r) => r.value));

  // All three classes are always listed; with nothing financed there is nothing to compare.
  if (max === 0) {
    return (
      <Box flex={1} display="flex" alignItems="center" justifyContent="center">
        <Typography fontSize="14px" fontWeight={400} lineHeight="24px" color="var(--gray1)">
          {t("Chart-No-Financed-Emissions")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      flex={1}
      display="flex"
      flexDirection="column"
      // Figma 7846:51918: rows sit 10px under the heading's gap, top-aligned.
      pt="10px"
      gap="12px"
    >
      {sorted.map((r, i) => {
        const label = t(assetClassLabelKey(r.assetClass));
        return (
          <Box key={r.assetClass} display="flex" alignItems="center" gap="8px">
            <Typography
              width="190px"
              flexShrink={0}
              fontSize="14px"
              fontWeight={500}
              lineHeight="24px"
              color="var(--dark)"
              noWrap
              title={label}
            >
              {label}
            </Typography>
            <Box
              flex={1}
              minWidth={0}
              height="20px"
              py="4px"
              boxSizing="border-box"
              display="flex"
            >
              <Box
                width={max > 0 ? `${(r.value / max) * 100}%` : 0}
                height="100%"
                bgcolor="var(--green)"
                sx={{ opacity: assetClassOpacity(i) }}
              />
            </Box>
            <Typography
              minWidth="88px"
              flexShrink={0}
              textAlign="right"
              fontSize="12px"
              fontWeight={600}
              lineHeight="16px"
              color="var(--dark)"
              whiteSpace="pre"
            >
              {`${formatNumber(r.value)}  tCO₂e`}
            </Typography>
            <Typography
              flexShrink={0}
              fontSize="12px"
              fontWeight={400}
              lineHeight="16px"
              color="var(--gray1)"
            >
              {`(${r.percent}%)`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default AssetClassBars;
